import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  ArrowLeft, Shield, ShieldAlert, ShieldCheck, TrendingDown, TrendingUp,
  AlertTriangle, Loader2, ChevronRight, Filter, BarChart3, Users,
} from 'lucide-react';
import { toast } from 'sonner';

const TIER_STYLES = {
  excellent: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  good: 'bg-cyan-100 text-cyan-800 border-cyan-200',
  review: 'bg-amber-100 text-amber-800 border-amber-200',
  concern: 'bg-red-100 text-red-800 border-red-200',
  'n/a': 'bg-slate-100 text-slate-600 border-slate-200',
};

const TierBadge = ({ tier }) => (
  <Badge className={TIER_STYLES[tier] || TIER_STYLES['n/a']} data-testid={`tier-${tier}`}>
    {tier === 'excellent' && <ShieldCheck className="w-3 h-3 mr-1" />}
    {tier === 'good' && <Shield className="w-3 h-3 mr-1" />}
    {tier === 'review' && <ShieldAlert className="w-3 h-3 mr-1" />}
    {tier === 'concern' && <AlertTriangle className="w-3 h-3 mr-1" />}
    {tier}
  </Badge>
);

/**
 * AdminReliabilityDashboard — Iter-266 (GPS Phase B)
 * Sortable list of every talent's reliability score with violation counts
 * + a platform-wide trend strip at the top.
 */
const AdminReliabilityDashboard = () => {
  const navigate = useNavigate();
  const [leaderboard, setLeaderboard] = useState({ results: [], total: 0 });
  const [trends, setTrends] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState('score_asc');
  const [tierFilter, setTierFilter] = useState('');
  const [minShifts, setMinShifts] = useState(1);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [lb, tr] = await Promise.all([
        api.get(`/gps/reliability/leaderboard?sort=${sort}&min_shifts=${minShifts}${tierFilter ? `&tier=${tierFilter}` : ''}&limit=200`),
        api.get('/gps/reliability/trends?weeks=4'),
      ]);
      setLeaderboard(lb.data || { results: [], total: 0 });
      setTrends(tr.data || null);
    } catch (e) {
      toast.error('Failed to load reliability data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [sort, tierFilter, minShifts]);

  const tierCounts = useMemo(() => {
    const counts = { excellent: 0, good: 0, review: 0, concern: 0 };
    leaderboard.results.forEach((r) => { if (counts[r.tier] !== undefined) counts[r.tier] += 1; });
    return counts;
  }, [leaderboard]);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-reliability-dashboard">
      <header className="bg-white border-b shadow-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="back-btn">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div className="flex-1">
            <h1 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-cyan-600" />
              Talent Reliability Dashboard
            </h1>
            <p className="text-xs text-slate-500">
              Score is computed from GPS compliance, attendance verification, early-leave events, recap completion.
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate('/admin/reliability/beta')}
            data-testid="beta-validation-btn"
            className="border-amber-200 text-amber-700 hover:bg-amber-50"
          >
            Beta Validation
          </Button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-6 space-y-6">

        {/* TRENDS STRIP */}
        {trends && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <Card data-testid="trends-platform-pct">
              <CardContent className="p-4">
                <div className="text-xs text-slate-500 uppercase">Platform Reliability</div>
                <div className="text-2xl font-bold text-slate-900 mt-1">
                  {trends.platform_reliability_pct !== null ? `${trends.platform_reliability_pct}%` : '—'}
                </div>
                <div className="text-xs text-slate-400">Last {trends.window_weeks} weeks</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="text-xs text-slate-500 uppercase">Shifts</div>
                <div className="text-2xl font-bold text-slate-900 mt-1">{trends.total_shifts}</div>
                <div className="text-xs text-slate-400">Last {trends.window_weeks} weeks</div>
              </CardContent>
            </Card>
            <Card data-testid="trends-violation-shifts">
              <CardContent className="p-4">
                <div className="text-xs text-slate-500 uppercase">Violation Shifts</div>
                <div className="text-2xl font-bold text-amber-700 mt-1">{trends.total_violation_shifts}</div>
                <div className="text-xs text-slate-400">GPS / attendance flagged</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="text-xs text-slate-500 uppercase">Talents Tracked</div>
                <div className="text-2xl font-bold text-slate-900 mt-1">{leaderboard.total}</div>
                <div className="text-xs text-slate-400">≥ {minShifts} shift{minShifts === 1 ? '' : 's'}</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="text-xs text-slate-500 uppercase">Needs Review</div>
                <div className="text-2xl font-bold text-amber-700 mt-1">
                  {tierCounts.review + tierCounts.concern}
                </div>
                <div className="text-xs text-slate-400">
                  {tierCounts.concern} concern · {tierCounts.review} review
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* TOP OFFENDERS */}
        {trends && trends.top_offenders?.length > 0 && (
          <Card data-testid="top-offenders-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <TrendingDown className="w-4 h-4 text-red-600" />
                Top Repeat Offenders (last {trends.window_weeks} weeks)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {trends.top_offenders.map((o) => (
                  <div
                    key={o.talent_id}
                    className="flex items-center gap-3 p-2 rounded hover:bg-slate-50 cursor-pointer"
                    onClick={() => navigate(`/admin/reliability/talent/${o.talent_id}`)}
                    data-testid={`offender-${o.talent_id}`}
                  >
                    {o.talent_photo ? (
                      <img src={o.talent_photo} alt={o.talent_name} className="w-8 h-8 rounded-full object-cover" />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center text-slate-500 text-xs font-bold">
                        {o.talent_name?.[0] || '?'}
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-slate-900">{o.talent_name}</div>
                      <div className="text-xs text-slate-500">
                        {o.violations} violations on {o.shifts} shifts · {o.violation_rate_pct}% rate
                      </div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-slate-300" />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* FILTERS */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <Filter className="w-4 h-4 text-slate-500" /> Filters
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex flex-wrap gap-2">
              <span className="text-xs text-slate-500 self-center mr-2">Sort:</span>
              {[
                { v: 'score_asc', label: 'Worst Score First' },
                { v: 'score_desc', label: 'Best Score First' },
                { v: 'violations_desc', label: 'Most Violations' },
                { v: 'shifts_desc', label: 'Most Shifts' },
              ].map((s) => (
                <Button
                  key={s.v}
                  variant={sort === s.v ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setSort(s.v)}
                  data-testid={`sort-${s.v}-btn`}
                  className={sort === s.v ? 'bg-cyan-600 hover:bg-cyan-700 text-white' : ''}
                >
                  {s.label}
                </Button>
              ))}
            </div>
            <div className="flex flex-wrap gap-2">
              <span className="text-xs text-slate-500 self-center mr-2">Tier:</span>
              {['', 'excellent', 'good', 'review', 'concern'].map((t) => (
                <Button
                  key={t || 'all'}
                  variant={tierFilter === t ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setTierFilter(t)}
                  data-testid={`tier-filter-${t || 'all'}-btn`}
                  className={tierFilter === t ? TIER_STYLES[t || 'good'] : ''}
                >
                  {t || 'All'}
                </Button>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-slate-500">Min shifts:</span>
              <select
                value={minShifts}
                onChange={(e) => setMinShifts(parseInt(e.target.value, 10))}
                className="border rounded px-2 py-1 text-sm"
                data-testid="min-shifts-select"
              >
                {[0, 1, 3, 5, 10].map((n) => (
                  <option key={n} value={n}>{n}+</option>
                ))}
              </select>
            </div>
          </CardContent>
        </Card>

        {/* LEADERBOARD */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <Users className="w-4 h-4 text-cyan-600" />
              Reliability Scores ({leaderboard.total})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="text-center py-12 text-slate-400">
                <Loader2 className="w-6 h-6 animate-spin inline mr-2" /> Loading…
              </div>
            ) : leaderboard.results.length === 0 ? (
              <div className="text-center py-12 text-slate-500">No talents match this filter.</div>
            ) : (
              <div className="divide-y">
                {leaderboard.results.map((r) => (
                  <div
                    key={r.talent_id}
                    className="flex items-center gap-3 px-4 py-3 hover:bg-slate-50 cursor-pointer"
                    onClick={() => navigate(`/admin/reliability/talent/${r.talent_id}`)}
                    data-testid={`talent-row-${r.talent_id}`}
                  >
                    {r.talent_photo ? (
                      <img src={r.talent_photo} alt={r.talent_name} className="w-9 h-9 rounded-full object-cover" />
                    ) : (
                      <div className="w-9 h-9 rounded-full bg-slate-200 flex items-center justify-center text-slate-500 font-bold">
                        {r.talent_name?.[0] || '?'}
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-slate-900">{r.talent_name || 'Unknown'}</div>
                      <div className="text-xs text-slate-500 flex flex-wrap gap-x-3 gap-y-0.5">
                        <span>{r.total_shifts} shifts</span>
                        {r.violations.gps_disabled > 0 && <span className="text-red-700">GPS off ×{r.violations.gps_disabled}</span>}
                        {r.violations.geofence_exit > 0 && <span className="text-amber-700">Geofence ×{r.violations.geofence_exit}</span>}
                        {r.violations.attendance_rejected > 0 && <span className="text-red-700">Rejected ×{r.violations.attendance_rejected}</span>}
                        {r.violations.early_leave > 0 && <span className="text-amber-700">Early ×{r.violations.early_leave}</span>}
                        {r.violations.no_clock_in > 0 && <span className="text-red-700">No clock-in ×{r.violations.no_clock_in}</span>}
                        {r.violations.recap_missing > 0 && <span className="text-slate-600">Missing recap ×{r.violations.recap_missing}</span>}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-xl font-bold text-slate-900">{r.reliability_score ?? '—'}</div>
                      <TierBadge tier={r.tier} />
                    </div>
                    <ChevronRight className="w-4 h-4 text-slate-300" />
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminReliabilityDashboard;
